import React, { useEffect, useMemo, useState, useRef } from "react";
import { FormField, FormService } from "@shared/services/FormServices";
import { SharedFormArray } from "@shared/components/SharedFormArray";
import { SegmentedControl } from "@shared/components/SegmentedControl";
import { Button } from "@shared/ui/button";
import { useForm, Controller, useWatch } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { useCurrency } from "@shared/selectors/globalSelectors";
import MultiSelectDropdown from "@shared/components/MultiSelectDropdown";

import { useParams } from "react-router-dom";
import { useGetPlanDetail, useUpdatePlan, useUtilityServices, useGetParameter, useGetParameterValues, useGetPlanDetailByTab } from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface DepositsProps {
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

const getSelectedServices = (val: any): string[] => {
  if (Array.isArray(val)) {
    return val
      .map((s: any) => (typeof s === "object" && s !== null ? (s.value ?? s.label ?? String(s)) : String(s)))
      .flatMap((s: string) => s.split(","))
      .map((s: string) => s.trim())
      .filter(Boolean);
  } else if (typeof val === "string") {
    return val.split(",").map((s) => s.trim()).filter(Boolean);
  } else if (val !== null && val !== undefined && val !== "") {
    return [String(val).trim()];
  }
  return [];
};

const getSelectedValues = (val: any): string[] => {
  if (Array.isArray(val)) {
    return val
      .map((s: any) => (typeof s === "object" && s !== null ? (s.value ?? s.label ?? s.key ?? String(s)) : String(s)))
      .flatMap((s: string) => s.split(","))
      .map((s: string) => s.trim())
      .filter(Boolean);
  } else if (typeof val === "string") {
    return val.split(",").map((s) => s.trim()).filter(Boolean);
  } else if (val !== null && val !== undefined && val !== "") {
    return [String(val).trim()];
  }
  return [];
};

export const Deposits: React.FC<DepositsProps> = ({
  data,
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
}) => {
  const { action, planId } = useParams();
  const [hasInitialData] = useState(() => Object.keys(data || {}).length > 0);
  const queryPlanId = hasInitialData ? undefined : planId;
  const { data: planDetail } = useGetPlanDetailByTab(queryPlanId, "deposits");
  const { mutateAsync: updatePlan, isPending } = useUpdatePlan();
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();
  const [mode, setMode] = useState("common");
  const [requireDeposit, setRequireDeposit] = useState(false);

  const { data: servicesResult } = useUtilityServices();
  const serviceOptions = useMemo(() => {
    const responseData = servicesResult?.result || servicesResult;
    if (!Array.isArray(responseData)) return [];
    return responseData
      .filter((service: any) => service.isActive === true)
      .map((service: any) => ({
        label: service.name,
        value: service.name,
      }));
  }, [servicesResult]);

  // Common Tiers
  const tierFields: FormField[] = [
    { name: "from", label: "From (MO)", type: "number", placeholder: "e.g. 0" },
    { name: "to", label: "To (MO)", type: "text", placeholder: "e.g. 12" },
    { name: "rate", label: "Rate (%)", type: "number", placeholder: "e.g. 1.5" },
  ];

  const form = useForm({
    defaultValues: data || {
      common: {
        depositName: "",
        depositAmount: "",
        payInterest: "no",
        eligibleAfter: "",
        eligiblePeriod: "months",
        tiers: [{ from: 0, to: 12, rate: 1 }],
      },
      parameterWise: {
        rules: [
          {
            parameter: "",
            value: "",
            depositName: "",
            amount: "",
            payInterest: "no",
            eligibleAfter: "",
            eligiblePeriod: "months",
            tiers: [{ from: 0, to: 12, rate: 1 }],
          },
        ],
      },
    },
    mode: "onChange",
  });

  const payInterestCommon = useWatch({
    control: form.control,
    name: "common.payInterest"
  });

  const formRules = useWatch({
    control: form.control,
    name: "parameterWise.rules"
  }) || [];

  const prevServicesRef = React.useRef<string[]>([]);
  const selectedUtilityServices = useMemo(() => {
    const allServices = new Set<string>();
    formRules.forEach((rule: any) => {
      const services = getSelectedServices(rule?.utilityService);
      services.forEach((s: string) => allServices.add(s));
    });

    const newServices = Array.from(allServices).sort();
    if (prevServicesRef.current.length !== newServices.length || !prevServicesRef.current.every((v, i) => v === newServices[i])) {
      prevServicesRef.current = newServices;
    }
    return prevServicesRef.current;
  }, [formRules]);

  const { data: parametersData } = useGetParameter(selectedUtilityServices);

  const allParametersList = useMemo(() => {
    const list = parametersData?.result || parametersData;
    return Array.isArray(list) ? list : [];
  }, [parametersData]);

  const parametersOptions = useMemo(() => {
    return allParametersList.map((p: any) => ({
      label: p.name,
      value: String(p.id),
      ...(p.allowed_values && p.allowed_values.length > 0 && { options: p.allowed_values.map((v: any) => ({ label: v, value: v })) })
    }));
  }, [allParametersList]);

  const getValueOptionsForRow = React.useCallback(
    (rowData: any) => {
      const selectedParamId = rowData?.parameter;
      if (!selectedParamId) return [];
      const sParamId = String(selectedParamId).toLowerCase();
      const param = allParametersList.find((p: any) => {
        return (
          String(p.id ?? "").toLowerCase() === sParamId ||
          String(p.parameter_id ?? "").toLowerCase() === sParamId ||
          String(p.name ?? "").toLowerCase() === sParamId ||
          String(p.code ?? "").toLowerCase() === sParamId ||
          String(p.key ?? "").toLowerCase() === sParamId
        );
      });
      if (!param) return [];
      const allowedValues =
        param?.allowedValues ||
        param?.allowed_values ||
        param?.allowedValuesOptions ||
        param?.values ||
        [];
      if (Array.isArray(allowedValues) && allowedValues.length > 0) {
        return allowedValues.map((val: any) => {
          if (typeof val === "object" && val !== null) {
            const label =
              val?.display ||
              val?.label ||
              val?.name ||
              val?.key ||
              String(val?.value || "");
            const value = String(val?.key ?? val?.value ?? val?.id ?? label);
            return { label, value };
          }
          return { label: String(val), value: String(val) };
        });
      }
      return [];
    },
    [allParametersList]
  );

  const ruleFieldsList: FormField[] = useMemo(() => [
    { name: "utilityService", label: "Utility Service", type: "multi-select", options: serviceOptions, className: "md:col-span-2" },
    { name: "parameter", label: "Parameter", type: "select", options: parametersOptions, className: "md:col-span-2" },
    { name: "value", label: "Value", type: "multi-select", getOptions: getValueOptionsForRow, className: "md:col-span-2" },
    { name: "depositName", label: "Deposite Name", type: "text", placeholder: "e.g. Industrial Deposit", className: "md:col-span-3" },
    { name: "amount", label: "Amount", type: "number", placeholder: "e.g. 500", className: "md:col-span-3" },
  ], [serviceOptions, parametersOptions, getValueOptionsForRow, currency]);

  const handleModeChange = (val: string) => {
    setMode(val);
  };

  const onDataChangeRef = useRef(onDataChange);
  const dataRef = useRef(data);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
    dataRef.current = data;
  }, [onDataChange, data]);

  useEffect(() => {
    if (!onDataChangeRef.current) return;
    onDataChangeRef.current({ ...dataRef.current, deposits: form.getValues(), mode, requireDeposit });
    const sub = form.watch((val) => {
      onDataChangeRef.current?.({ ...dataRef.current, deposits: val, mode, requireDeposit });
    });
    return () => sub.unsubscribe();
  }, [form, mode, requireDeposit]);

  const isPopulated = React.useRef(false);
  const isLocalStorageRestored = React.useRef(false);

  useEffect(() => {
    if (hasInitialData && data && !isLocalStorageRestored.current) {
      if (data.mode) setMode(data.mode);
      if (data.requireDeposit !== undefined) setRequireDeposit(data.requireDeposit);
      if (data.deposits) {
        const paramRules = data.deposits.parameterWise?.rules;
        const normalizedParamRules = Array.isArray(paramRules)
          ? paramRules.map((rule: any) => ({
              ...rule,
              utilityService: getSelectedServices(rule.utilityService || rule.utilityServices || rule.utility_services),
              value: getSelectedValues(rule.value || rule.parameterValues || rule.parameter_values),
            }))
          : paramRules;
        const normalizedDeposits = {
          ...data.deposits,
          ...(normalizedParamRules ? { parameterWise: { ...data.deposits.parameterWise, rules: normalizedParamRules } } : {}),
        };
        FormService.populateForm(form as any, normalizedDeposits, [
          { name: 'common', type: 'custom' },
          { name: 'parameterWise', type: 'custom' }
        ]);
      }
      isLocalStorageRestored.current = true;
    }
  }, [hasInitialData, data, form]);

  useEffect(() => {
    isPopulated.current = false;
  }, [planId]);

  useEffect(() => {
    if (hasInitialData) return;
    if (planDetail?.result && planId && planId !== "create" && !isPopulated.current) {
      const res = planDetail.result;

      if (res.enabled !== undefined) {
        setRequireDeposit(res.enabled);
      }

      const rulesGrouped = res.rulesGrouped || res.rules_grouped || [];
      // The deposits_config API returns a flat `rules` array - support that too
      const flatRules: any[] = res.rules || [];
      const mappedData: any = {};

      if (rulesGrouped.length > 0) {
        // grouped shape (rulesGrouped)
        const firstGroup = rulesGrouped[0];
        const firstRule = (firstGroup.rules || [])[0];
        const ruleMode = firstRule?.mode?.toLowerCase()?.replace('-', '_');

        if (firstRule && ruleMode === 'common') {
          setMode("common");
          mappedData.common = {
            depositName: firstRule.depositName || firstRule.deposit_name || "",
            depositAmount: firstRule.amount,
            payInterest: (res.enabled && (firstRule.payInterest || firstRule.pay_interest)) ? "yes" : "no",
            eligibleAfter: firstRule.eligibleAfter ?? firstRule.eligible_after ?? "",
            eligiblePeriod: firstRule.eligibleAfterUnit || firstRule.eligible_after_unit || "months",
            tiers: (firstRule.interestTiers || firstRule.interest_tiers || []).map((t: any) => ({
              from: t.fromMonth || t.from_month || 0,
              to: t.toMonth || t.to_month || "",
              rate: t.rate || 0
            }))
          };
        } else if (firstRule && (ruleMode === 'parameter_wise' || ruleMode === 'parameter-wise')) {
          setMode("parameter-wise");
          const allRules = rulesGrouped.flatMap((group: any) => {
            return (group.rules || []).map((rule: any) => ({
              parameter: String(group.parameterId || group.parameter_id || rule.parameterId || rule.parameter_id || rule.parameter || ""),
              value: getSelectedValues(rule.parameterValues || rule.parameter_values || rule.value),
              utilityService: getSelectedServices(rule.utilityServices || rule.utility_services || rule.utilityService),
              depositName: rule.depositName || rule.deposit_name || "",
              amount: rule.amount,
              payInterest: (res.enabled && (rule.payInterest || rule.pay_interest)) ? "yes" : "no",
              eligibleAfter: rule.eligibleAfter ?? rule.eligible_after ?? "",
              eligiblePeriod: rule.eligibleAfterUnit || rule.eligible_after_unit || "months",
              tiers: (rule.interestTiers || rule.interest_tiers || []).map((t: any) => ({
                from: t.fromMonth || t.from_month || 0,
                to: t.toMonth || t.to_month || "",
                rate: t.rate || 0
              }))
            }));
          });
          mappedData.parameterWise = { rules: allRules };
        }
      } else if (flatRules.length > 0) {
        // flat shape (deposits_config.rules)
        const firstRule = flatRules[0];
        const ruleMode = firstRule?.mode?.toLowerCase()?.replace('-', '_');

        if (ruleMode === 'common' || !ruleMode) {
          setMode("common");
          mappedData.common = {
            depositName: firstRule.depositName || firstRule.deposit_name || "",
            depositAmount: firstRule.depositAmount ?? firstRule.amount ?? "",
            payInterest: (res.enabled && (firstRule.payInterest || firstRule.pay_interest)) ? "yes" : "no",
            eligibleAfter: firstRule.eligibleAfter ?? firstRule.eligible_after ?? "",
            eligiblePeriod: firstRule.eligibleAfterUnit || firstRule.eligible_after_unit || "months",
            tiers: (firstRule.interestTiers || firstRule.interest_tiers || []).map((t: any) => ({
              from: t.fromMonth || t.from_month || 0,
              to: t.toMonth || t.to_month || "",
              rate: t.rate || 0
            }))
          };
        } else if (ruleMode === 'parameter_wise' || ruleMode === 'parameter-wise') {
          setMode("parameter-wise");
          const allRules = flatRules.map((rule: any) => ({
            parameter: String(rule.parameterId || rule.parameter_id || rule.parameter || ""),
            value: getSelectedValues(rule.parameterValues || rule.parameter_values || rule.value),
            utilityService: getSelectedServices(rule.utilityServices || rule.utility_services || rule.utilityService),
            depositName: rule.depositName || rule.deposit_name || "",
            amount: rule.amount,
            payInterest: (res.enabled && (rule.payInterest || rule.pay_interest)) ? "yes" : "no",
            eligibleAfter: rule.eligibleAfter ?? rule.eligible_after ?? "",
            eligiblePeriod: rule.eligibleAfterUnit || rule.eligible_after_unit || "months",
            tiers: (rule.interestTiers || rule.interest_tiers || []).map((t: any) => ({
              from: t.fromMonth || t.from_month || 0,
              to: t.toMonth || t.to_month || "",
              rate: t.rate || 0
            }))
          }));
          mappedData.parameterWise = { rules: allRules };
        }
      }

      FormService.populateForm(form as any, mappedData, [
        { name: 'common', type: 'custom' },
        { name: 'parameterWise', type: 'custom' }
      ] as any[]);

      isPopulated.current = true;
    }
  }, [planDetail, planId, form, parametersData]);

  const validateAndNext = async () => {
    const valid = await form.trigger();
    if (valid) {
      if (planId && planId !== "create") {
        try {
          const formData = form.getValues();
          const rules: any[] = [];


          if (requireDeposit) {
            if (mode === "common") {
              rules.push({
                id: "dep-001",
                mode: "common",
                deposit_name: formData.common?.depositName || "",
                basis: "flat",
                amount: Number(formData.common?.depositAmount || 0),
                multiplier: null,
                pay_interest: formData.common?.payInterest === "yes",
                eligible_after: Number(formData.common?.eligibleAfter || 0),
                eligible_after_unit: formData.common?.eligiblePeriod || "months",
                interest_tiers: formData.common?.payInterest === "yes"
                  ? (formData.common?.tiers || []).map((t: any) => ({
                    from_month: Number(t.from),
                    to_month: t.to ? Number(t.to) : null,
                    rate: Number(t.rate)
                  }))
                  : []
              });
            } else if (mode === "parameter-wise") {
              const paramRules = formData.parameterWise?.rules || [];
              const paramList = Array.isArray(parametersData?.result || parametersData) ? (parametersData?.result || parametersData) : [];
              paramRules.forEach((rule: any, index: number) => {
                const paramObj = paramList.find((p: any) => String(p.id) === String(rule.parameter));
                const paramCode = paramObj?.code || rule.code || rule.parameter_code || "";
                rules.push({
                  id: `dep-00${index + 1}`,
                  mode: "parameter_wise",
                  utility_services: getSelectedServices(rule.utilityService),
                  parameter_id: rule.parameter,
                  parameter_code: paramCode,
                  parameter: rule.parameter,
                  parameter_values: getSelectedValues(rule.value || rule.parameter_values || rule.parameterValues),
                  deposit_name: rule.depositName,
                  basis: "flat",
                  amount: Number(rule.amount || 0),
                  multiplier: null,
                  pay_interest: rule.payInterest === "yes",
                  eligible_after: Number(rule.eligibleAfter || 0),
                  eligible_after_unit: rule.eligiblePeriod || "months",
                  interest_tiers: rule.payInterest === "yes"
                    ? (rule.tiers || []).map((t: any) => ({
                      from_month: Number(t.from),
                      to_month: t.to ? Number(t.to) : null,
                      rate: Number(t.rate)
                    }))
                    : [],
                  priority: index + 1
                });
              });
            }
          }

          const payload = {
            current_step: 5,
            remote_utility_id: remoteUtilityId,
            deposits_config: {
              enabled: requireDeposit,
              rules: rules
            }
          };

          if (action !== 'edit') {
            await updatePlan({ planId, payload });
          }
          if (onNext) onNext();
        } catch (error) {
          console.error("Failed to update deposits config:", error);
        }
      } else {
        if (onNext) onNext();
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Deposit Settings
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            Configure security or advance deposits for new consumers. In Common
            mode, set one flat amount for all. In Parameter-wise mode, vary the
            deposit by customer class, meter size, income tier, etc.
          </p>
        </div>
        <SegmentedControl
          options={[
            { label: "Common", value: "common" },
            { label: "Parameter-wise", value: "parameter-wise" },
          ]}
          value={mode}
          onChange={handleModeChange}
        />
      </div>

      <div className="pt-2">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            checked={requireDeposit}
            onChange={(e) => setRequireDeposit(e.target.checked)}
          />
          <div>
            <div className="font-semibold text-sm text-gray-900">
              Require a deposit for new accounts
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              When enabled, new consumers assigned to this plan are charged a
              deposit at signup.
            </div>
          </div>
        </label>
      </div>

      {requireDeposit && mode === "common" && (
        <div className="space-y-6 w-full">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Deposit Name
              </label>
              <input
                {...form.register("common.depositName")}
                className="w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm py-2.5 px-3"
                placeholder="e.g. Security Deposit"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Deposit Amount
              </label>
              <div className="relative">
                <input
                  {...form.register("common.depositAmount")}
                  type="number"
                  placeholder="e.g. 500"
                  className={`w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm py-2.5 pr-3 ${currency ? "pl-8" : "pl-3"
                    }`}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                Single flat deposit amount applied to all new consumers on this
                plan.
              </p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Pay Interest
              </label>
              <select
                {...form.register("common.payInterest")}
                className="w-full border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm py-2.5 px-3"
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>

              </select>
            </div>
          </div>

          {payInterestCommon === "yes" && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                  Eligible after
                </label>
                <input
                  {...form.register("common.eligibleAfter")}
                  type="number"
                  placeholder="e.g. 6"
                  className="w-20 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm py-2 px-3"
                />
                <select
                  {...form.register("common.eligiblePeriod")}
                  className="w-32 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm py-2.5 px-3"
                >
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>

              <div className="mt-2">
                <SharedFormArray
                  form={form}
                  name="common.tiers"
                  itemFields={tierFields}
                  rowIndexName="Tier"
                  variant="table"
                  addButtonText="Add tier"
                  gridColumns={3}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {requireDeposit && mode === "parameter-wise" && (
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Deposit Determinant Rules
            </h3>
          </div>
          <p className="text-sm text-gray-500 -mt-2 mb-4">
            Each rule sets the deposit amount when a consumer's parameter matches
            — e.g. Customer Class = Industrial → 2x monthly bill, or Income Tier =
            Lifeline → waived. Each rule decides its own interest schedule
            independently.
          </p>

          <SharedFormArray
            name="parameterWise.rules"
            form={form}
            itemFields={ruleFieldsList}
            variant="card"
            gridColumns={12}
            addButtonText="Add Rule"
            minItems={1}
            emptyMessage="No rules added."
            defaultItemFactory={() => ({
              parameter: "",
              value: "",
              depositName: "",
              amount: "",
              payInterest: "no",
              eligibleAfter: "",
              eligiblePeriod: "months",
              tiers: [{ from: 0, to: 12, rate: 1 }],
            })}
            renderAfterContent={(index) => {
              const rulePayInterest = form.watch(
                `parameterWise.rules.${index}.payInterest`
              );
              return (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-gray-500  tracking-wider">
                      Pay Interest
                    </label>
                    <select
                      {...form.register(`parameterWise.rules.${index}.payInterest`)}
                      className="w-full max-w-[120px] border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm py-2 px-3"
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                    {rulePayInterest === "yes" && (
                      <span className="text-xs text-gray-500 ml-2">
                        {form.watch(`parameterWise.rules.${index}.tiers`)?.length || 0} tiers on this deposit
                      </span>
                    )}
                  </div>

                  {rulePayInterest === "yes" && (
                    <div className="mt-5 space-y-4">
                      <div className="flex items-center gap-3">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Eligible After
                        </label>
                        <input
                          {...form.register(
                            `parameterWise.rules.${index}.eligibleAfter`
                          )}
                          type="number"
                          placeholder="e.g. 6"
                          className="w-16 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm py-1.5 px-2"
                        />
                        <select
                          {...form.register(`parameterWise.rules.${index}.eligiblePeriod`)}
                          className="w-28 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm py-1.5 px-2"
                        >
                          <option value="months">Months</option>
                          <option value="years">Years</option>
                        </select>
                        <span className="text-xs text-gray-500 ml-1">
                          Interest accrues from day one.
                        </span>
                      </div>

                      <div className="mt-2">
                        <SharedFormArray
                          form={form}
                          name={`parameterWise.rules.${index}.tiers`}
                          itemFields={tierFields}
                          rowIndexName="Tier"
                          variant="table"
                          addButtonText="Add tier"
                          gridColumns={3}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            }}
          />
        </div>
      )}

      <div className="flex items-center justify-between mt-6 border-t pt-6">
        <div>
          {!isFirstStep && onPrevious && (
            <Button type="button" variant="outline" onClick={onPrevious}>
              Previous
            </Button>
          )}
        </div>
        <Button type="button" onClick={validateAndNext} disabled={isPending}>
          {isPending ? "Saving..." : (isLastStep ? "Finish" : "Next")}
        </Button>
      </div>
    </div>
  );
};
